/**
 * ========================
 * ASSET MANAGEMENT DOMAIN MENU PROVIDER
 * ========================
 *
 * Two audiences (mirrors the Booking/Members menu shape):
 *   - STAFF (Staff / AssetReader): My Assets dashboard, Scan to Find, My Records.
 *   - ADMIN (AssetAdmin / Admin): Asset Operations + Administration.
 */

import {
  IconHome,
  IconScan,
  IconClipboardList,
  IconLayoutDashboard,
  IconBox,
  IconArrowsExchange,
  IconFileImport,
  IconReportAnalytics,
  IconDatabase,
  IconSettings,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck, anyVisibilityCheck } from './BaseMenuProvider';
import { useAssetBadgesStore } from '../badges/assetBadgesStore';

const ASSET_ADMIN_ROLES = ['AssetAdmin', 'Admin'];

class AssetManagementMenuProvider implements DomainMenuProvider {
  domainId = 'assetmanagement';

  getMenuConfig(): DomainMenuConfig {
    const allUsers = createVisibilityCheck();
    const assetAdmin = anyVisibilityCheck({ roles: ASSET_ADMIN_ROLES }, { permissions: ['Asset.Manage'] });

    const sections: MenuSection[] = [
      // ---------- STAFF: My Workspace ----------
      {
        id: 'asset-my',
        label: 'My Workspace',
        visible: allUsers,
        items: [
          { id: 'asset-my-dashboard', label: 'Dashboard', path: '/app/assetmanagement', icon: <IconHome size={16} />, visible: allUsers },
          { id: 'asset-scan', label: 'Scan to Find', path: '/app/assetmanagement/scan', icon: <IconScan size={16} />, visible: allUsers },
        ],
      },
      // ---------- STAFF: My Records ----------
      {
        id: 'asset-my-records',
        label: 'My Records',
        visible: allUsers,
        items: [
          {
            id: 'asset-my-assets', label: 'My Assets', path: '/app/assetmanagement/my-assets',
            icon: <IconClipboardList size={16} />, visible: allUsers,
            countSelector: () => useAssetBadgesStore.getState().myAssets || null, badgeColor: 'blue',
          },
        ],
      },

      // ---------- ADMIN: Asset Operations ----------
      {
        id: 'asset-ops',
        label: 'Asset Operations',
        visible: assetAdmin,
        items: [
          {
            id: 'asset-admin-overview', label: 'Overview', path: '/app/assetmanagement/admin/overview',
            icon: <IconLayoutDashboard size={16} />, visible: assetAdmin, requiredRoles: ASSET_ADMIN_ROLES,
            countSelector: () => useAssetBadgesStore.getState().maintenanceDue || null, badgeColor: 'yellow',
          },
          {
            id: 'asset-admin-all', label: 'All Assets', path: '/app/assetmanagement/list',
            icon: <IconBox size={16} />, visible: assetAdmin, requiredRoles: ASSET_ADMIN_ROLES,
            countSelector: () => useAssetBadgesStore.getState().unacknowledged || null, badgeColor: 'red',
          },
          { id: 'asset-admin-transfers', label: 'Transfers', path: '/app/assetmanagement/admin/transfers', icon: <IconArrowsExchange size={16} />, visible: assetAdmin, requiredRoles: ASSET_ADMIN_ROLES },
          { id: 'asset-admin-import', label: 'Import Assets', path: '/app/assetmanagement/bulk-import', icon: <IconFileImport size={16} />, visible: assetAdmin, requiredRoles: ASSET_ADMIN_ROLES },
        ],
      },
      // ---------- ADMIN: Administration ----------
      {
        id: 'asset-admin',
        label: 'Administration',
        visible: assetAdmin,
        items: [
          { id: 'asset-admin-reports', label: 'Reports', path: '/app/assetmanagement/admin/reports', icon: <IconReportAnalytics size={16} />, visible: assetAdmin, requiredRoles: ASSET_ADMIN_ROLES },
          { id: 'asset-admin-lookups', label: 'Lookups', path: '/app/assetmanagement/admin/lookups', icon: <IconDatabase size={16} />, visible: assetAdmin, requiredRoles: ASSET_ADMIN_ROLES },
          { id: 'asset-admin-settings', label: 'Settings', path: '/app/assetmanagement/admin/settings', icon: <IconSettings size={16} />, visible: assetAdmin, requiredRoles: ASSET_ADMIN_ROLES },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: { version: '2.0.0', lastUpdated: 0, description: 'Asset Management — staff self-service + AssetAdmin operations' },
    };
  }
}

export const assetmanagementMenuProvider = new AssetManagementMenuProvider();
